
import {
  CartLinesUpdateEvent,
  StandardEvents
} from '@shopify/events';

/**
 * BOLTON COUTURE
 * Recomendaciones para el carrito de Horizon.
 *
 * Primera recomendacion: Camiseta Sorpresa.
 * Otras dos: recomendaciones automaticas de Shopify.
 */

const MYSTERY_HANDLE = 'camiseta-sorpresa-futbol';
const MAX_RECOMMENDATIONS = 3;

class BoltonCartRecommendations extends HTMLElement {
  _dialogObserver = null;
  _listObserver = null;
  _debounce = null;
  _requestSequence = 0;
  _ignoreListChanges = false;

  _boundCartUpdate = (event) => {
    if (event.promise) {
      event.promise
        .then(() => this._scheduleLoad())
        .catch(() => {});
    } else {
      this._scheduleLoad();
    }
  };

  _boundClick = (event) => this._onClick(event);

  connectedCallback() {
    this.addEventListener('click', this._boundClick);

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this._boundCartUpdate
    );

    this._dialog = this.closest('dialog');

    if (this._dialog) {
      this._dialogObserver = new MutationObserver(() => {
        if (this._dialog?.open) {
          this.load();
        }
      });

      this._dialogObserver.observe(this._dialog, {
        attributes: true,
        attributeFilter: ['open']
      });
    }

    // Cargar incluso si Horizon ya abrio el carrito.
    this.load();

    this._listObserver = new MutationObserver(() => {
      if (
        this._ignoreListChanges ||
        !this.isConnected ||
        (this._dialog && !this._dialog.open)
      ) {
        return;
      }

      const list = this.querySelector(
        '[data-bc-recommendations-list]'
      );

      if (list && list.childElementCount === 0) {
        this._scheduleLoad();
      }
    });

    this._listObserver.observe(this, {
      childList: true,
      subtree: true
    });
  }

  disconnectedCallback() {
    this._requestSequence++;

    this._dialogObserver?.disconnect();
    this._listObserver?.disconnect();

    this._dialogObserver = null;
    this._listObserver = null;

    clearTimeout(this._debounce);

    this.removeEventListener(
      'click',
      this._boundClick
    );

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this._boundCartUpdate
    );
  }

  _scheduleLoad() {
    clearTimeout(this._debounce);

    this._debounce = setTimeout(() => {
      if (
        this.isConnected &&
        (!this._dialog || this._dialog.open)
      ) {
        this.load();
      }
    }, 180);
  }

  get _root() {
    return window.Shopify?.routes?.root || '/';
  }

  async _json(url, options) {
    const response = await fetch(url, options);

    const data = await response.json();

    if (!response.ok || data.status >= 400) {
      throw new Error(
        data.description ||
        data.message ||
        `HTTP ${response.status}`
      );
    }

    return data;
  }

  _money(cents, currency) {
    try {
      const locale =
        document.documentElement.lang || 'es-ES';

      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency || 'EUR'
      }).format((Number(cents) || 0) / 100);
    } catch {
      return (
        ((Number(cents) || 0) / 100).toFixed(2) +
        ' EUR'
      );
    }
  }

  async _getMysteryProduct() {
    try {
      return await this._json(
        `${this._root}products/${MYSTERY_HANDLE}.js`
      );
    } catch (error) {
      console.warn(
        'Bolton Couture: Camiseta Sorpresa no disponible',
        error
      );

      return null;
    }
  }

  async load() {
    if (!this.isConnected) return;

    const sequence = ++this._requestSequence;

    const list = this.querySelector(
      '[data-bc-recommendations-list]'
    );

    const errorElement = this.querySelector(
      '[data-bc-recommendations-error]'
    );

    if (!list) return;

    if (errorElement) {
      errorElement.hidden = true;
    }

    try {
      const cart = await this._json(
        `${this._root}cart.js`
      );

      if (
        sequence !== this._requestSequence ||
        !this.isConnected
      ) {
        return;
      }

      if (!cart.items?.length) {
        this.hidden = true;
        list.replaceChildren();
        return;
      }

      const inCart = new Set(
        cart.items.map((item) =>
          String(item.product_id)
        )
      );

      const seen = new Set(inCart);
      const products = [];

      // Primera recomendacion: Camiseta Sorpresa.
      const mystery = await this._getMysteryProduct();

      if (
        sequence !== this._requestSequence ||
        !this.isConnected
      ) {
        return;
      }

      if (
        mystery &&
        mystery.available &&
        !seen.has(String(mystery.id)) &&
        (mystery.variants || []).some(
          (variant) => variant.available
        )
      ) {
        seen.add(String(mystery.id));
        products.push(mystery);
      }

      // Completar con recomendaciones de Shopify.
      const anchors = [
        ...new Set(
          cart.items.map((item) =>
            String(item.product_id)
          )
        )
      ].slice(0, 4);

      for (const productId of anchors) {
        if (products.length >= MAX_RECOMMENDATIONS) {
          break;
        }

        const params = new URLSearchParams({
          product_id: productId,
          limit: '10',
          intent: 'related'
        });

        let result;

        try {
          result = await this._json(
            `${this._root}recommendations/products.json?${params}`
          );
        } catch (error) {
          console.warn(
            'Bolton Couture: error al cargar sugerencias',
            error
          );

          continue;
        }

        if (
          sequence !== this._requestSequence ||
          !this.isConnected
        ) {
          return;
        }

        for (const product of result.products || []) {
          if (
            !product.available ||
            seen.has(String(product.id))
          ) {
            continue;
          }

          if (
            !(product.variants || []).some(
              (variant) => variant.available
            )
          ) {
            continue;
          }

          seen.add(String(product.id));
          products.push(product);

          if (products.length >= MAX_RECOMMENDATIONS) {
            break;
          }
        }
      }

      if (
        sequence !== this._requestSequence ||
        !this.isConnected
      ) {
        return;
      }

      this._ignoreListChanges = true;

      list.replaceChildren(
        ...products.map((product) =>
          this._card(product, cart.currency)
        )
      );

      this.hidden = products.length === 0;

      queueMicrotask(() => {
        this._ignoreListChanges = false;
      });

    } catch (error) {
      console.warn(
        'Bolton Couture: no se pudieron cargar las recomendaciones',
        error
      );

      if (sequence === this._requestSequence) {
        this.hidden = true;
      }
    }
  }

  _card(product, currency) {
    const card = document.createElement('div');

    card.className = 'bc-cart-recommendations__item';
    card.dataset.productId = String(product.id);

    const isMystery =
      product.handle === MYSTERY_HANDLE;

    if (isMystery) {
      card.classList.add(
        'bc-cart-recommendations__mystery'
      );
    }

    const productUrl =
      product.url ||
      `${this._root}products/${product.handle}`;

    const imageLink = document.createElement('a');

    imageLink.href = productUrl;
    imageLink.setAttribute(
      'aria-label',
      product.title
    );

    const image = document.createElement('img');

    image.className =
      'bc-cart-recommendations__image';

    image.loading = 'lazy';
    image.alt = product.title;

    const featured = product.featured_image;

    image.src =
      (typeof featured === 'string'
        ? featured
        : featured?.src) ||
      product.images?.[0]?.src ||
      product.images?.[0] ||
      '';

    imageLink.append(image);
    card.append(imageLink);

    const info = document.createElement('div');

    info.className =
      'bc-cart-recommendations__info';

    const name = document.createElement('a');

    name.className =
      'bc-cart-recommendations__name';

    name.href = productUrl;
    name.textContent = product.title;

    info.append(name);

    const price = document.createElement('span');

    price.className =
      'bc-cart-recommendations__price';

    info.append(price);

    const variants = (
      product.variants || []
    ).filter(
      (variant) => variant.available
    );

    const select = document.createElement('select');

    select.className =
      'bc-cart-recommendations__variant';

    select.setAttribute(
      'aria-label',
      `Selecciona la talla para ${product.title}`
    );

    for (const variant of variants) {
      const option = document.createElement('option');

      option.value = String(variant.id);

      option.textContent =
        variant.public_title ||
        variant.title ||
        'Opcion unica';

      select.append(option);
    }

    if (variants.length <= 1) {
      select.hidden = true;
    }

    info.append(select);

    const updatePrice = () => {
      const variant =
        variants.find(
          (item) =>
            String(item.id) === select.value
        ) || variants[0];

      price.replaceChildren();

      if (!variant) return;

      price.append(
        document.createTextNode(
          this._money(variant.price, currency)
        )
      );

      if (
        variant.compare_at_price >
        variant.price
      ) {
        const original = document.createElement('span');

        original.className =
          'bc-cart-recommendations__compare';

        original.textContent = this._money(
          variant.compare_at_price,
          currency
        );

        price.append(original);
      }
    };

    select.addEventListener(
      'change',
      updatePrice
    );

    updatePrice();

    card.append(info);

    const add = document.createElement('button');

    add.type = 'button';
    add.className = 'bc-cart-recommendations__add';

    add.textContent = '+ Añadir';
    add.dataset.bcAdd = '1';

    add.disabled = variants.length === 0;

    card.append(add);

    return card;
  }

  async _onClick(event) {
    const button = event.target.closest?.(
      '[data-bc-add]'
    );

    if (
      !button ||
      button.disabled ||
      !this.contains(button)
    ) {
      return;
    }

    const card = button.closest(
      '[data-product-id]'
    );

    const select = card?.querySelector('select');

    const variantId = Number(select?.value);

    if (!variantId) return;

    const originalLabel = button.textContent;

    const errorElement = this.querySelector(
      '[data-bc-recommendations-error]'
    );

    if (errorElement) {
      errorElement.hidden = true;
    }

    button.disabled = true;
    button.textContent = 'Añadiendo...';

    // Crear un evento compatible con Horizon actualizado.
    const deferred = CartLinesUpdateEvent.createPromise();

    // Avisar al tema antes de realizar la peticion.
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [
          {
            merchandiseId: String(variantId),
            quantity: 1
          }
        ],
        promise: deferred.promise
      })
    );

    try {
      // Identificar las secciones del carrito.
      const sectionIds = [
        ...new Set(
          [
            ...document.querySelectorAll(
              'cart-items-component'
            )
          ]
            .map(
              (element) => element.dataset.sectionId
            )
            .filter(Boolean)
        )
      ];

      // Añadir la variante y solicitar secciones actualizadas.
      const response = await this._json(
        `${this._root}cart/add.js`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: [
              {
                id: variantId,
                quantity: 1
              }
            ],
            sections: sectionIds.join(','),
            sections_url: window.location.pathname
          })
        }
      );

      // Consultar el carrito real despues de añadir.
      const cart = await this._json(
        `${this._root}cart.js`
      );

      // Resolver el evento para que Horizon actualice
      // contador, productos, subtotales y otras secciones.
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(
          cart
        ),
        detail: {
          sections: response.sections || undefined,
          items: cart.items,
          itemCount: cart.item_count,
          source: 'bc-cart-recommendations',
          didError: false
        }
      });

      this._scheduleLoad();

    } catch (caught) {
      console.error(
        'Bolton Couture: error al añadir recomendacion',
        caught
      );

      deferred.reject(caught);

      if (errorElement?.isConnected) {
        errorElement.textContent =
          'No se pudo añadir el producto. Comprueba la talla y el stock disponible.';

        errorElement.hidden = false;
      }

    } finally {
      if (button.isConnected) {
        button.disabled = false;
        button.textContent = originalLabel;
      }
    }
  }
}

if (!customElements.get('bc-cart-recommendations')) {
  customElements.define(
    'bc-cart-recommendations',
    BoltonCartRecommendations
  );
}
